import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Swords, Trophy, Flame, Award } from "lucide-react";

export default function Duel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/duel");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>;
  }
  if (!data) return null;

  const { totals, bleus_top, rouges_top } = data;
  const bleusPct =
    totals.matches_played > 0
      ? Math.round((totals.bleus_wins / totals.matches_played) * 100)
      : 0;
  const rougesPct =
    totals.matches_played > 0
      ? Math.round((totals.rouges_wins / totals.matches_played) * 100)
      : 0;
  const drawsPct =
    totals.matches_played > 0
      ? Math.round((totals.draws / totals.matches_played) * 100)
      : 0;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
          Le classique
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight flex items-center gap-3">
          <Swords className="w-10 h-10 text-[#E5B85C]" />
          Duel Bleus vs Rouges
        </h1>
      </div>

      {totals.matches_played === 0 ? (
        <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-slate-500">
          Aucun match terminé pour l'instant
        </div>
      ) : (
        <>
          {/* Score global */}
          <div className="rounded-2xl bg-slate-900 border border-white/10 p-6 sm:p-8">
            <div className="grid grid-cols-3 gap-4 items-center text-center">
              <div data-testid="bleus-wins">
                <div className="text-xs font-mono-stat uppercase tracking-widest text-sky-400 mb-2">
                  Bleus
                </div>
                <div className="font-display font-black text-6xl sm:text-7xl text-sky-400 leading-none">
                  {totals.bleus_wins}
                </div>
                <div className="text-xs text-slate-500 font-mono-stat mt-2">
                  {bleusPct}% win
                </div>
              </div>
              <div data-testid="draws">
                <div className="text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
                  Nuls
                </div>
                <div className="font-display font-black text-4xl sm:text-5xl text-slate-400 leading-none">
                  {totals.draws}
                </div>
                <div className="text-xs text-slate-500 font-mono-stat mt-2">
                  {drawsPct}%
                </div>
              </div>
              <div data-testid="rouges-wins">
                <div className="text-xs font-mono-stat uppercase tracking-widest text-rose-400 mb-2">
                  Rouges
                </div>
                <div className="font-display font-black text-6xl sm:text-7xl text-rose-400 leading-none">
                  {totals.rouges_wins}
                </div>
                <div className="text-xs text-slate-500 font-mono-stat mt-2">
                  {rougesPct}% win
                </div>
              </div>
            </div>

            {/* Bar */}
            <div className="mt-8 h-3 rounded-full bg-slate-800 overflow-hidden flex">
              <div
                className="bg-sky-500"
                style={{ width: `${bleusPct}%` }}
                data-testid="bleus-bar"
              />
              <div
                className="bg-slate-500"
                style={{ width: `${drawsPct}%` }}
              />
              <div
                className="bg-rose-500"
                style={{ width: `${rougesPct}%` }}
                data-testid="rouges-bar"
              />
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <MiniStat label="Matchs joués" value={totals.matches_played} color="#E5B85C" />
              <MiniStat label="Buts Bleus" value={totals.bleus_goals} color="#60A5FA" />
              <MiniStat label="Buts Rouges" value={totals.rouges_goals} color="#DC2626" />
              <MiniStat
                label="Différence"
                value={totals.bleus_goals - totals.rouges_goals}
                color="#94A3B8"
              />
            </div>
          </div>

          {/* Streaks */}
          {data.streaks && (data.streaks.current_length > 0 || data.streaks.longest_bleus > 0 || data.streaks.longest_rouges > 0) && (
            <div>
              <h2 className="font-display font-bold text-xl uppercase mb-4 text-slate-300 flex items-center gap-2">
                <Flame className="w-5 h-5 text-[#FF9944]" />
                Séries
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StreakCard
                  label="Série en cours"
                  side={data.streaks.current_side}
                  length={data.streaks.current_length}
                  testid="streak-current"
                  current
                />
                <StreakCard
                  label="Meilleure Bleus"
                  side="bleus"
                  length={data.streaks.longest_bleus}
                  testid="streak-bleus"
                />
                <StreakCard
                  label="Meilleure Rouges"
                  side="rouges"
                  length={data.streaks.longest_rouges}
                  testid="streak-rouges"
                />
              </div>
            </div>
          )}

          {/* Top scorers per side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TopList title="Buteurs Bleus" side="bleus" list={bleus_top} />
            <TopList title="Buteurs Rouges" side="rouges" list={rouges_top} />
          </div>

          {/* Podium Capitaine */}
          {data.captains_podium && data.captains_podium.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-xl uppercase mb-4 text-slate-300 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#E5B85C]" />
                Podium Capitaine
              </h2>
              <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
                <div className="divide-y divide-white/5">
                  {data.captains_podium.map((c, i) => (
                    <div
                      key={c.player_id}
                      data-testid={`captains-podium-row-${i}`}
                      className="p-4 flex items-center gap-3"
                    >
                      <div
                        className="w-10 h-10 rounded flex items-center justify-center font-display font-black text-lg"
                        style={{
                          background:
                            i === 0 ? "#E5B85C" : i === 1 ? "rgba(148,163,184,0.25)" : "rgba(180,83,9,0.25)",
                          color: i === 0 ? "#0B0F17" : i === 1 ? "#e2e8f0" : "#fbbf24",
                        }}
                      >
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm truncate flex items-center gap-1.5">
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-black bg-[#E5B85C] text-[#0B0F17]">
                            C
                          </span>
                          {c.player_name}
                          {c.jersey_number != null && (
                            <span className="text-xs text-slate-500 font-mono-stat">
                              · #{c.jersey_number}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 font-mono-stat flex gap-2 mt-0.5">
                          <span className="text-sky-400">{c.bleus} × Bleus</span>
                          <span className="text-slate-600">·</span>
                          <span className="text-rose-400">{c.rouges} × Rouges</span>
                        </div>
                      </div>
                      <div className="font-display font-black text-2xl text-[#E5B85C]">
                        {c.count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Monthly trophies */}
          {data.monthly && data.monthly.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-xl uppercase mb-4 text-slate-300 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#E5B85C]" />
                Trophées mensuels
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.monthly.map((m) => (
                  <MonthlyCard key={m.month} month={m} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MonthlyCard({ month }) {  const [y, mo] = month.month.split("-");
  const monthName = new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
  const winnerMeta =
    month.winner === "bleus"
      ? { label: "Les Bleus", color: "#60A5FA" }
      : month.winner === "rouges"
      ? { label: "Les Rouges", color: "#DC2626" }
      : { label: "Ex æquo", color: "#94A3B8" };
  return (
    <div
      data-testid={`monthly-${month.month}`}
      className="p-4 rounded-xl bg-slate-900 border border-white/10 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: winnerMeta.color }} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
            {monthName}
          </div>
          <div
            className="font-display font-black text-2xl uppercase mt-1"
            style={{ color: winnerMeta.color }}
          >
            {winnerMeta.label}
          </div>
        </div>
        <Trophy className="w-6 h-6" style={{ color: winnerMeta.color }} />
      </div>
      <div className="mt-3 flex gap-2 text-xs font-mono-stat text-slate-400">
        <span className="text-sky-400">{month.bleus_wins}V</span>
        <span>·</span>
        <span>{month.draws}N</span>
        <span>·</span>
        <span className="text-rose-400">{month.rouges_wins}V</span>
        <span className="ml-auto">{month.matches} match(s)</span>
      </div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="p-3 rounded-lg bg-slate-800">
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div className="font-display font-black text-2xl mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function TopList({ title, side, list }) {
  const color = side === "bleus" ? "#60A5FA" : "#DC2626";
  return (
    <div
      data-testid={`top-${side}`}
      className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden"
    >
      <div
        className="p-4 border-b border-white/10 flex items-center gap-2"
        style={{ background: `${color}15` }}
      >
        <Trophy className="w-4 h-4" style={{ color }} />
        <div className="font-display font-bold text-sm uppercase tracking-wide">{title}</div>
      </div>
      <div className="divide-y divide-white/5">
        {list.length === 0 && (
          <div className="p-6 text-center text-slate-500 text-sm">Aucun buteur enregistré</div>
        )}
        {list.slice(0, 8).map((p, i) => (
          <div key={p.player_id} className="p-3 flex items-center gap-3">
            <div
              className="w-8 h-8 rounded font-mono-stat font-bold text-xs flex items-center justify-center"
              style={{ background: `${color}30`, color }}
            >
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{p.player_name}</div>
              <div className="text-xs text-slate-500 font-mono-stat">
                {p.assists} passe(s) déc.
              </div>
            </div>
            <div
              className="font-display font-black text-xl"
              style={{ color }}
            >
              {p.goals}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StreakCard({ label, side, length, testid, current }) {
  const color = side === "bleus" ? "#60A5FA" : side === "rouges" ? "#DC2626" : "#94A3B8";
  const sideLabel = side === "bleus" ? "Les Bleus" : side === "rouges" ? "Les Rouges" : "—";
  return (
    <div
      data-testid={testid}
      className="p-5 rounded-xl bg-slate-900 border border-white/10 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: color }} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
            {label}
          </div>
          {current && length > 0 ? (
            <div className="font-display font-black text-xl mt-1" style={{ color }}>
              {sideLabel}
            </div>
          ) : (
            <div className="text-xs text-slate-500 font-mono-stat mt-1">
              {current ? "Aucune" : sideLabel}
            </div>
          )}
        </div>
        <Flame className="w-5 h-5" style={{ color }} />
      </div>
      <div
        className="font-display font-black text-5xl mt-3"
        style={{ color }}
      >
        {length}
      </div>
      <div className="text-xs text-slate-500 font-mono-stat mt-1">
        victoire{length > 1 ? "s" : ""} d'affilée
      </div>
    </div>
  );
}

